import { http } from '@/lib/http';
import type {
  CreatePolicyInput,
  Policy,
  PolicyVersion,
  TransportMode,
  UpdatePolicyInput,
} from './types';

export async function listPolicies(params: { active?: boolean } = {}): Promise<Policy[]> {
  const search = new URLSearchParams();
  if (params.active !== undefined) search.set('active', String(params.active));
  const qs = search.toString();
  return http.get(`v1/policies${qs ? `?${qs}` : ''}`).json<Policy[]>();
}

export async function getPolicy(policyUrn: string): Promise<Policy> {
  return http.get(`v1/policies/${encodeURIComponent(policyUrn)}`).json<Policy>();
}

export async function createPolicy(input: CreatePolicyInput): Promise<Policy> {
  return http.post('v1/policies', { json: input }).json<Policy>();
}

export async function updatePolicy(
  policyUrn: string,
  input: UpdatePolicyInput,
): Promise<Policy> {
  return http
    .put(`v1/policies/${encodeURIComponent(policyUrn)}`, { json: input })
    .json<Policy>();
}

export async function setPolicyActive(policyUrn: string, active: boolean): Promise<Policy> {
  return http
    .post(`v1/policies/${encodeURIComponent(policyUrn)}/${active ? 'activate' : 'deactivate'}`)
    .json<Policy>();
}

export async function listPolicyVersions(policyUrn: string): Promise<PolicyVersion[]> {
  return http
    .get(`v1/policies/${encodeURIComponent(policyUrn)}/versions`)
    .json<PolicyVersion[]>();
}

/**
 * The backend PERSISTS the AI draft (inactive, policies-tds §11) and returns
 * the created policy — navigate to it; do not re-create it via POST.
 */
export async function generatePolicy(prompt: string): Promise<Policy> {
  return http.post('v1/policies/generate-ai', { json: { prompt } }).json<Policy>();
}

// --- Resolution explorer (POL-12): which policy wins for a context + why ---

export interface ResolveCandidate {
  urn: string;
  name: string;
  priority: number;
  specificity: number;
  matchType: string;
}

export interface ResolveExplain {
  reason: string;
  winner: ResolveCandidate;
  candidatesConsidered: number;
  runnersUp: ResolveCandidate[];
}

export interface ResolveResult {
  /** false when no active policy matches the context. */
  resolved: boolean;
  policyUrn?: string;
  policyName?: string;
  matchType?: string;
  explain?: ResolveExplain;
  /** The winner's ground-transport mode for this context (BFF, additive) —
   *  the read `apps/transport` makes before an AUTOMATIC dispatch. Absent when
   *  the policy does not state one, or on an older BFF. */
  transportMode?: TransportMode;
}

/** Scope dims the operator narrows on; the airline is pinned + injected by the BFF. */
export interface ResolveQuery {
  airport?: string;
  country?: string;
  aircraft?: string;
  fare?: string;
  leg?: string;
  tier?: string;
  /** ISO-8601 instant to resolve at (`operator-bff:GET /v1/policies/resolve@v2`,
   *  additive) — "which policy wins on 20 Dec". Absent = now. */
  asOf?: string;
}

export async function resolvePolicy(params: ResolveQuery): Promise<ResolveResult> {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) search.set(k, v);
  }
  const qs = search.toString();
  return http.get(`v1/policies/resolve${qs ? `?${qs}` : ''}`).json<ResolveResult>();
}
